import React, {useEffect, useMemo, useState} from 'react';
import {
    getLatestClusters,
    LatestClusterProperties,
    LatestClusters
} from '../../../data-access/latest-clusters';
import {MapboxMap} from '../../reusable/map/map';
import {MapLoadingGuard} from '@zvne/fe-framework/dist/presentation/map/guards/map-loading-guard';
import {MapEventListener} from '@zvne/fe-framework/dist/presentation/map/misc/map-event-listener';
import {SimpleGeoJSONSource} from '../../reusable/map/simple-geojson-source';
import {Point, Polygon} from 'geojson';
import {getEmptyFeatureCollection} from '@zvne/fe-framework/dist/utilities/geometry';
import {Layer, MapContext, Popup} from 'react-mapbox-gl';
import {useTheme} from '@zvne/fe-framework/dist/presentation/theme/theme-context';
import {LegendControl} from '@zvne/fe-framework/dist/presentation/map/controls/legend-control';
import {useStrings} from '@zvne/fe-framework/dist/presentation/language/strings';
import {strings} from './strings';
import {dateTimeToShortUserString, numberToUserString, dateToUserString, formatDate} from '@zvne/fe-framework/dist/utilities/type-conversion';
import {useLanguage} from '@zvne/fe-framework/dist/presentation/language/language';
import {useSubmitApi} from '@zvne/fe-framework/dist/data-access/use-submit-api';
import {DropdownControl} from '@zvne/fe-framework/dist/presentation/map/controls/dropdown-control';
import {CheckboxDropdownItem} from '@zvne/fe-framework/dist/presentation/form/input/checkbox-dropdown-item';
import {NullGuard} from '@zvne/fe-framework/dist/presentation/map/guards/null-guard';

const refreshInterval = 60000;

const emptyLatestClusters: LatestClusters = {
    intervalSizeMinutes: 10,
    lastUpdates: [],
    polygons: getEmptyFeatureCollection<Polygon, LatestClusterProperties>(),
    centroids: getEmptyFeatureCollection<Point, LatestClusterProperties>()
};

export function LiveScreen() {
    const [apiClusters, refresh] = useSubmitApi(getLatestClusters);
    useEffect(() => {
        refresh({});
        const intervalId = setInterval(() => refresh({}), refreshInterval);
        return () => clearInterval(intervalId);
    }, [refresh]);

    const [clusters, setClusters] = useState<LatestClusters | null>(null);
    useEffect(() => {
        if (typeof apiClusters === 'object') {
            setClusters(apiClusters);
        }
    }, [apiClusters]);

    const [disabledTimeIds, setDisabledTimeIds] = useState<number[]>([]);
    const isTimeIdEnabled = (timeId: number) => disabledTimeIds.findIndex(t => t === timeId) === -1;
    const setTimeIdEnabled = (timeId: number, newEnabled: boolean) => {
        if (newEnabled === isTimeIdEnabled(timeId)) {
            return;
        }
        if (newEnabled) {
            const index = disabledTimeIds.indexOf(timeId);
            const newArray = [...disabledTimeIds];
            newArray.splice(index, 1);
            setDisabledTimeIds(newArray);
        } else {
            setDisabledTimeIds([...disabledTimeIds, timeId]);
        }
    };

    const [selectedCluster, setSelectedCluster] = useState<LatestClusterProperties | null>(null);

    const theme = useTheme();
    const language = useLanguage();
    const s = useStrings(strings);

    const intervalMessage = useMemo(() => {
        if (clusters == null) {
            return null;
        }
        const lastUpdate = clusters.lastUpdates.find(l => l.timeId === 0);
        if (lastUpdate == null) {
            return null;
        }
        const timestamp = lastUpdate.timestamp;
        const dateString = dateToUserString(timestamp, language);
        const timeString = formatDate(timestamp, 'HH:mm');
        return `${dateString} ${timeString}`;
    }, [clusters, language]);

    const timeIdToLegendString = (timeId: number) => {
        if (timeId === 0) {
            return s.current;
        }
        const intervalSizeMinutes = clusters != null ? clusters.intervalSizeMinutes : 10;
        const stringValue = numberToUserString(timeId * intervalSizeMinutes, language);
        const prefix = timeId > 0 ? '+' : '';
        return prefix + stringValue + ' ' + s.min;
    };

    const realClusterColor = theme === 'dark' ? '#00ff00' : '#008800';
    const predictedClusterColor = theme === 'dark' ? '#ffff00' : '#888800';

    return (
        <MapboxMap containerStyle={{height: '100%'}}>
            <MapLoadingGuard
                value={apiClusters}
                defaultValue={emptyLatestClusters}
                content={() => null}
            />
            <MapEventListener
                type={'click'}
                listener={(e, map) => {
                    const features = map.queryRenderedFeatures(
                        e.point,
                        {layers: ['polygons-layer']}
                    );
                    if (features.length === 0) {
                        setSelectedCluster(null);
                        return;
                    }
                    const properties = features[features.length - 1].properties as LatestClusterProperties;
                    if (selectedCluster != null &&
                        properties.clusterId === selectedCluster.clusterId &&
                        properties.prediction === selectedCluster.prediction) {
                        setSelectedCluster(null);
                        return;
                    }
                    setSelectedCluster(properties);
                }}
            />
            <NullGuard
                value={clusters}
                defaultValue={emptyLatestClusters}
                content={clusters =>
                    <>
                        <DropdownControl id={'layers'} title={s.layers}>
                            {clusters.lastUpdates.map(l =>
                                <CheckboxDropdownItem
                                    key={l.timeId}
                                    eventKey={`timeId ${l.timeId}`}
                                    value={isTimeIdEnabled(l.timeId)}
                                    onChange={v => setTimeIdEnabled(l.timeId, v)}
                                >
                                    {timeIdToLegendString(l.timeId)}
                                </CheckboxDropdownItem>
                            )}
                        </DropdownControl>
                        <LegendControl title={s.liveClusters}>
                            <div style={{textAlign: 'right'}}>
                                {intervalMessage}
                            </div>
                            {clusters.lastUpdates.map(l => isTimeIdEnabled(l.timeId) &&
                                <div key={l.timeId} style={{textAlign: 'right'}}>
                                    {timeIdToLegendString(l.timeId)}
                                    &nbsp;
                                    <div
                                        style={{
                                            display: 'inline-block',
                                            width: 12,
                                            height: 12,
                                            border: '1px solid black',
                                            margin: 0,
                                            backgroundColor: (l.timeId <= 0 ? realClusterColor : predictedClusterColor) +
                                                (Math.abs(l.timeId) <= 0 ? 'aa' : (Math.abs(l.timeId) <= 1 ? '88' : '55'))
                                        }}
                                    />
                                </div>
                            )}
                        </LegendControl>
                        <SimpleGeoJSONSource
                            id={'polygons-source'}
                            data={clusters.polygons}
                        />
                        <SimpleGeoJSONSource
                            id={'centroids-source'}
                            data={clusters.centroids}
                        />
                        <MapContext.Consumer>
                            {map => map != null &&
                                <Layer
                                    id={'polygons-layer'}
                                    sourceId={'polygons-source'}
                                    type={'fill'}
                                    filter={['!', ['in', ['get', 'timeId'], ['literal', disabledTimeIds]]]}
                                    layout={{}}
                                    paint={{
                                        'fill-color': ['case',
                                            ['<=', ['get', 'prediction'], 0], realClusterColor,
                                            predictedClusterColor
                                        ],
                                        'fill-opacity': ['case',
                                            ['<=', ['abs', ['get', 'timeId']], 0], 0.5,
                                            ['<=', ['abs', ['get', 'timeId']], 1], 0.35,
                                            ['<=', ['abs', ['get', 'timeId']], 2], 0.2,
                                            0.1
                                        ]
                                    }}
                                    onMouseEnter={() => map.getCanvas().style.cursor = 'pointer'}
                                    onMouseLeave={() => map.getCanvas().style.removeProperty('cursor')}
                                />
                            }
                        </MapContext.Consumer>
                        <Layer
                            sourceId={'polygons-source'}
                            type={'line'}
                            filter={['!', ['in', ['get', 'timeId'], ['literal', disabledTimeIds]]]}
                            layout={{
                                'line-cap': 'round'
                            }}
                            paint={{
                                'line-width': 2,
                                'line-color': ['case',
                                    ['<=', ['get', 'prediction'], 0], realClusterColor,
                                    predictedClusterColor
                                ],
                                'line-opacity': ['case',
                                    ['<=', ['abs', ['get', 'timeId']], 0], 0.625,
                                    ['<=', ['abs', ['get', 'timeId']], 1], 0.4375,
                                    ['<=', ['abs', ['get', 'timeId']], 2], 0.25,
                                    0.125
                                ]
                            }}
                        />
                        <Layer
                            sourceId={'centroids-source'}
                            type={'circle'}
                            filter={['!', ['in', ['get', 'timeId'], ['literal', disabledTimeIds]]]}
                            layout={{}}
                            paint={{
                                'circle-radius': 3,
                                'circle-color': ['case',
                                    ['<=', ['get', 'prediction'], 0], realClusterColor,
                                    predictedClusterColor
                                ],
                                'circle-opacity': ['case',
                                    ['<=', ['abs', ['get', 'timeId']], 0], 0.625,
                                    ['<=', ['abs', ['get', 'timeId']], 1], 0.4375,
                                    ['<=', ['abs', ['get', 'timeId']], 2], 0.25,
                                    0.125
                                ]
                            }}
                        />
                    </>
                }
            />
            {selectedCluster != null &&
            <Popup coordinates={[selectedCluster.centroidX, selectedCluster.centroidY]}>
                <p style={{margin: 0}}>
                    {selectedCluster.timeId <= 0 ? s.reality : s.prediction}:
                    <br/>
                    {dateTimeToShortUserString(selectedCluster.timestamp, language)}
                    &nbsp;
                    (<b>{timeIdToLegendString(selectedCluster.timeId)}</b>)
                    <br/>
                    <b>{numberToUserString(selectedCluster.strokeCount, language)}</b>
                    &nbsp;
                    {s.strokes}
                </p>
            </Popup>
            }
        </MapboxMap>
    );
}
